'use client';

import { useEffect, useRef } from 'react';
import { usePromptContext } from '@/providers/PromptProvider';
import type { TriggerPageType } from '@/lib/prompts/storage';

/**
 * Called from trigger pages on mount. Guards against firing twice for
 * the same slug (React strict mode dev double-effect, re-mounts).
 *
 * - `pageType`: which page category the caller is (unit / home / building / pseo).
 * - `slug`: unique identifier for dedupe (unit_id, building_slug, pathname for pseo).
 *   For `home` pass any stable string — home uses a time-based debounce, not slug-based.
 * - `reportReturnUrl`: only meaningful on surfaces that fire the report prompt
 *   (unit / building). The URL to send the user to after a successful subscription
 *   purchase (typically the building's report page). Persisted in localStorage when
 *   the user clicks the report prompt CTA so the success page can redirect
 *   regardless of what happens during Stripe checkout.
 */
export function usePromptTrigger(
  pageType: TriggerPageType,
  slug: string,
  reportReturnUrl?: string,
): void {
  const { onPageView } = usePromptContext();
  const lastFiredKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    const key = `${pageType}:${slug}`;
    if (lastFiredKeyRef.current === key) return;
    lastFiredKeyRef.current = key;
    onPageView(pageType, slug, reportReturnUrl);
  }, [pageType, slug, reportReturnUrl, onPageView]);
}
